import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardBody } from "../../../components/Card/Card";
import { Alert } from "../../../components/Alert/Alert";
import { ApiError, describeError } from "../../../lib/errors";
import { Clock } from "lucide-react";
import { fetchActivity } from "./api";
import { renderEntry } from "./renderEntry";
import type { ActivityItem } from "./types";
import styles from "./ActivityLogCard.module.css";

type Props = { projectId: string };

function describeUnknown(err: unknown): string {
  if (err instanceof ApiError) return describeError(err.code);
  return describeError("internal");
}

export function ActivityLogCard({ projectId }: Props) {
  // Page 1 is owned by TanStack Query so external invalidations refetch it.
  // Subsequent pages live in local state and are appended to the visible feed.
  const first = useQuery({
    queryKey: ["project", projectId, "activity"],
    queryFn: () => fetchActivity(projectId, null, 10),
  });

  const [extra, setExtra] = useState<ActivityItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);

  // Reset extra pages whenever the first page reloads. Identifying a fresh
  // first page by the top-most item id + dataUpdatedAt is enough — query
  // invalidation always produces a new dataUpdatedAt.
  const seedKey = first.data
    ? `${first.dataUpdatedAt}-${first.data.items[0]?.id ?? ""}`
    : "";
  useEffect(() => {
    if (!first.data) return;
    setExtra([]);
    setNextCursor(first.data.next_cursor);
    setOlderError(null);
  }, [seedKey, first.data]);

  if (first.isLoading) {
    return (
      <Card>
        <CardHeader>
          <h3>Activity log</h3>
        </CardHeader>
        <CardBody className={styles.body}>
          <ul className={styles.feed} aria-busy="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className={styles.skeletonRow}>
                <div className={styles.skeletonChip} />
                <div className={styles.skeletonLines}>
                  <div
                    className={styles.skeletonLine}
                    style={{ width: "70%" }}
                  />
                  <div
                    className={styles.skeletonLine}
                    style={{ width: "40%" }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    );
  }

  if (first.isError) {
    return (
      <Card>
        <CardHeader>
          <h3>Activity log</h3>
        </CardHeader>
        <CardBody className={styles.body}>
          <Alert variant="danger" title="Couldn't load activity">
            {describeUnknown(first.error)}
          </Alert>
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              type="button"
              onClick={() => first.refetch()}
            >
              Retry
            </button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const items = [...(first.data?.items ?? []), ...extra];

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h3>Activity log</h3>
        </CardHeader>
        <CardBody className={styles.body}>
          <div className={styles.empty}>
            <div className={styles.emptyChip} aria-hidden="true">
              <Clock size={22} />
            </div>
            <div className={styles.emptyTitle}>No activity yet</div>
            <div className={styles.emptyBody}>
              Actions show up here as your team works.
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  async function onShowMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    setOlderError(null);
    try {
      const page = await fetchActivity(projectId, nextCursor, 10);
      setExtra((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch {
      setOlderError("Failed to load older entries");
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <h3>Activity log</h3>
      </CardHeader>
      <CardBody className={styles.body}>
        <ul className={styles.feed}>
          {items.map((item) => {
            const { icon: Icon, phrase, time } = renderEntry(item);
            const meta = item.actor.name ?? item.actor.email;
            return (
              <li key={item.id} className={styles.row}>
                <span className={styles.chip} aria-hidden="true">
                  <Icon size={16} />
                </span>
                <div className={styles.content}>
                  <div className={styles.phrase}>{phrase}</div>
                  <div className={styles.meta}>
                    {meta} · {time}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {olderError && <div className={styles.inlineError}>{olderError}</div>}
        {nextCursor && (
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              type="button"
              disabled={loadingMore}
              onClick={onShowMore}
            >
              {loadingMore ? "Loading…" : "Show more"}
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
